import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Box,
  CircularProgress
} from '@mui/material';

const REASONS = [
  "No longer need the services",
  "Found a better alternative",
  "Privacy concerns",
  "Too expensive",
  "Missing expected features",
  "Technical issues or bugs",
  "Other"
];

interface DeleteProfileModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: { reason: string; comments: string }) => Promise<void>;
}

const DeleteProfileModal: React.FC<DeleteProfileModalProps> = ({ open, onClose, onConfirm }) => {
  const [reason, setReason] = useState("");
  const [comments, setComments] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!reason) return;
    setLoading(true);
    await onConfirm({ reason, comments });
    setLoading(false);
  };

  return (
    <Dialog
      open={open}
      onClose={!loading ? onClose : undefined}
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 2,
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)"
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 'bold', color: '#1c234b', pb: 1 }}>
        We're sorry to see you go
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.primary" sx={{ mb: 3 }}>
          Before you delete your profile permanently, could you please let us know why you are leaving? Your feedback is very important to us.
        </Typography>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="deletion-reason-label">Reason for leaving</InputLabel>
          <Select
            labelId="deletion-reason-label"
            value={reason}
            label="Reason for leaving"
            onChange={(e) => setReason(e.target.value)}
          >
            {REASONS.map((r, i) => (
              <MenuItem key={i} value={r}>{r}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Additional Feedback (Optional)"
          multiline
          rows={3}
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          placeholder="What could we have done better?"
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{ color: '#666', fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Box sx={{ position: 'relative' }}>
          <Button 
            variant="contained" 
            color="error" 
            disabled={!reason || loading}
            onClick={handleSubmit}
            sx={{ 
              fontWeight: 600, 
              borderRadius: 2,
              minWidth: '180px',
              height: '40px'
            }}
          >
            {loading ? "" : "Permanently Delete Profile"}
          </Button>
          {loading && (
            <CircularProgress
              size={24}
              sx={{
                color: 'error.main',
                position: 'absolute',
                top: '50%',
                left: '50%',
                marginTop: '-12px',
                marginLeft: '-12px',
              }}
            />
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteProfileModal;
